import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  LocalHospital,
  ArrowBack,
  ExitToApp,
  MedicalServices,
  FitnessCenter,
  Work,
  PregnantWoman,
} from '@mui/icons-material';
import { authService } from '../../services/api';

const REPORT_TYPES = [
  {
    key: 'medical-leave',
    title: 'Medical Leave Certificate',
    description: 'Issue a certificate for medical leave',
    icon: <MedicalServices sx={{ fontSize: 48 }} />,
    color: '#1565c0',
    bg: '#e3f2fd',
  },
  {
    key: 'fitness-general',
    title: 'Fitness Certificate (General)',
    description: 'General fitness certificate for a patient',
    icon: <FitnessCenter sx={{ fontSize: 48 }} />,
    color: '#2e7d32',
    bg: '#e8f5e9',
  },
  {
    key: 'fitness-job',
    title: 'Medical Fitness Certificate (Job)',
    description: 'Fitness certificate for employment purposes',
    icon: <Work sx={{ fontSize: 48 }} />,
    color: '#e65100',
    bg: '#fff3e0',
  },
  {
    key: 'pregnancy',
    title: 'Pregnancy Certificate',
    description: 'Bed rest certificate for pregnant patients',
    icon: <PregnantWoman sx={{ fontSize: 48 }} />,
    color: '#c2185b',
    bg: '#fce4ec',
  },
  {
    key: 'medical-leave-form3',
    title: 'Medical Leave Certificate (Form 3)',
    description: 'Govt. Form 3 medical leave certificate',
    icon: <MedicalServices sx={{ fontSize: 48 }} />,
    color: '#6a1b9a',
    bg: '#f3e5f5',
  },
  {
    key: 'fitness-form4',
    title: 'Fitness Certificate (Form 4)',
    description: 'Fitness certificate confirming recovery and duty resumption',
    icon: <FitnessCenter sx={{ fontSize: 48 }} />,
    color: '#00695c',
    bg: '#e0f2f1',
  },
  {
    key: 'medical-form5',
    title: 'Medical Certificate (Form 5)',
    description: 'Medical certificate with fitness opinion and rest recommendation',
    icon: <Work sx={{ fontSize: 48 }} />,
    color: '#4527a0',
    bg: '#ede7f6',
  },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => { authService.logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Reports
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={handleMenu} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Select Report Type
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Choose the type of certificate or report you want to generate.
        </Typography>

        <Grid container spacing={3}>
          {REPORT_TYPES.map((report) => (
            <Grid item xs={12} sm={6} md={4} key={report.key}>
              <Card
                elevation={3}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  border: `1px solid ${report.bg}`,
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}
              >
                <CardActionArea
                  sx={{ height: '100%', p: 1 }}
                  onClick={() => navigate(`/reports/${report.key}`)}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: report.bg,
                        color: report.color,
                        mb: 2,
                      }}
                    >
                      {report.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: report.color }}>
                      {report.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
};

export default ReportsPage;
